import LaunchIcon from '@mui/icons-material/Launch';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import ContactUsForm from '@/components/contactUs/contactUsForm';
import DataGridTable from '@/components/datagridTable';
import DrawerPanel from '@/components/drawerPanel';
import ErrorMessage from '@/components/errorMessage';
import ProgressIndicator from '@/components/progressIndicator';
import { useApiContext } from '@/contexts/apiContext';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { formatRelative } from 'date-fns';

const UserAppDefs = () => {
  const apiContext = useApiContext();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [userAppDefs, setUserAppDefs] = useState<any>([]);
  const [apiError, setApiError] = useState<any>();
  const [loading, setLoading] = useState(false);

  const [showContactUs, setShowContactUs] = useState(false);

  const hideContactUs = () => {
    setShowContactUs(false);
  };

  useEffect(() => {
    const getUserAppDefs = async () => {
      try {
        setLoading(true);
        const userAppdefs = await apiContext.getUserAppDefs();
        setUserAppDefs(userAppdefs);
      } catch (error: any) {
        setApiError(error);
      }
      setLoading(false);
    };
    getUserAppDefs();
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: t('userApp.userAppDefs.columns.id'),
      width: 200,
    },
    {
      field: 'name',
      headerName: t('userApp.userAppDefs.columns.name'),
      flex: 1,
    },
    {
      field: 'domainId',
      headerName: 'Domain',
      width: 140,
      valueFormatter: (value: string) => value || 'general',
    },
    {
      field: 'implementationStack',
      headerName: t('userApp.userAppDefs.columns.implementationStack'),
      width: 200,
    },
    {
      field: 'createdBy',
      headerName: t('userApp.userAppDefs.columns.createdBy'),
      width: 200,
      valueFormatter: (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : ''),
    },
    {
      field: 'createdDate',
      headerName: t('userApp.userAppDefs.columns.createdDate'),
      width: 200,
      valueFormatter: (value: string) => (value ? formatRelative(new Date(value), new Date()) : ''),
    },
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      getActions: (params: any) => [
        <GridActionsCellItem icon={<LaunchIcon />} label="Open" onClick={() => viewUserAppDef(params.row)} />,
      ],
    },
  ];

  const viewUserAppDef = (userAppdef: any) => {
    navigate(`/userAppdef/${userAppdef.id}`);
  };

  const createUserAppDef = () => {
    setShowContactUs(true);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
        bgcolor: 'background.paper',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('userApp.userAppDefs.header')}
          </Typography>
          {apiError && <ErrorMessage error={apiError} />}
        </Stack>
        <Button
          size="small"
          color="secondary"
          variant="contained"
          onClick={createUserAppDef}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
            color: 'text.primary',
          }}
        >
          {t('userApp.userAppDefs.actions.create')}
        </Button>
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
        {loading ? (
          <ProgressIndicator />
        ) : (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {userAppDefs.length === 0 && <Typography sx={{ p: 1 }}>{t('userApp.userAppDefs.noRecord')}</Typography>}
            {userAppDefs.length > 0 && <DataGridTable rows={userAppDefs} columns={columns} loading={loading} />}
          </Box>
        )}
        <DrawerPanel open={showContactUs} onClose={hideContactUs} minWidth={'40vw'}>
          <ContactUsForm onClose={() => setShowContactUs(false)} />
        </DrawerPanel>
      </Box>
    </Box>
  );
};

export default UserAppDefs;
